import React from "react"

interface Props {
  children: React.ReactNode
}

interface State {
  hasError: boolean
  error: Error | null
  errorInfo: React.ErrorInfo | null
}

export class ErrorBoundary extends React.Component<Props, State> {
  public state: State = {
    hasError: false,
    error: null,
    errorInfo: null
  }

  public static getDerivedStateFromError(error: Error): State {
    // Update state so the next render will show the fallback UI.
    return { hasError: true, error, errorInfo: null }
  }

  public componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    console.error("Uncaught error:", error, errorInfo)
    this.setState({
      error,
      errorInfo
    })
  }

  public render() {
    if (this.state.hasError) {
      return (
        <div className="flex flex-col items-center justify-center min-h-screen bg-red-50 p-6 z-[9999] relative">
          <div className="bg-white p-6 rounded-lg shadow-xl max-w-full w-full overflow-hidden border border-red-200">
            <h1 className="text-xl font-bold text-red-600 mb-4 flex items-center gap-2">
              <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="m21.73 18-8-14a2 2 0 0 0-3.48 0l-8 14A2 2 0 0 0 4 21h16a2 2 0 0 0 1.73-3Z"/><path d="M12 9v4"/><path d="M12 17h.01"/></svg>
              Critical Rendering Error
            </h1>
            <p className="text-sm font-medium text-gray-700 mb-2">The application crashed during boot.</p>
            
            <div className="bg-gray-100 p-4 rounded text-xs font-mono overflow-auto max-h-48 whitespace-pre-wrap mb-4 text-red-800">
              {this.state.error && this.state.error.toString()}
            </div>
            
            {this.state.errorInfo && (
              <div className="bg-gray-100 p-4 rounded text-xs font-mono overflow-auto max-h-48 whitespace-pre-wrap text-gray-600">
                {this.state.errorInfo.componentStack}
              </div>
            )}
            
            <button 
              onClick={() => window.location.reload()}
              className="mt-6 w-full bg-red-600 hover:bg-red-700 text-white font-medium py-2 px-4 rounded transition-colors"
            >
              Reload Application
            </button>
          </div>
        </div>
      )
    }

    return this.props.children
  }
}
